import { Control } from '@Common/Controls/Controls';
import { Button } from '@Common/Controls/SimpleButton';
import constants from '@Common/Core/Constant';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import { IPopupControl } from '@Common/Helpers/CommonUtils';
import pointerHelper from '@Common/Helpers/PointerHelper';
import StylesHelper from '@Common/Helpers/StylesHelper';
import WindowResizeHelper from '@Common/Helpers/WindowResizeHelper';
import { ViewAppearance } from '@InstanceFactory';
import Guid from '@Common/Core/Guid';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface IDialogParameters {
    useResizeLock?: boolean;
    clipToWindow?: boolean;
    className?: string;
    css?: any;

    isModal?: boolean;
    fullScreen?: boolean;

    hideOk?: boolean;
    hideCancel?: boolean;
    cancelText?: string;
    okText?: string;
    title?: string;
    onClose?: () => void;

    width?: any;
    height?: any;
    minWidth?: any;
    maxWidth?: any;
    minHeight?: number;
    contentWidth?: any;
    contentHeight?: any;

    closeByEnter?: boolean;
    closeByEsc?: boolean;

    usePaddings?: boolean;
    noHeaderLine?: boolean;

    buttons?: any[];

    canSwitchToFullScreen?: boolean;
    resizable?: boolean;
    resizeOnFly?: boolean;

    useDetach?: boolean;
    /***
     * true by default
     */
    destroyWraped?: boolean;
    canClose?: boolean;

    parentViewAppearance?: ViewAppearance;

    stylesHelper?: StylesHelper;
}

export interface IDialog {
    wrap(control: any);

    wrapView(view: Control);

    center();

    getButton(id): any;

    getActualHeight(): number;

    remove();

    close(); // same as remove. Why?
    ok(handler: () => any);

    onClose(handler: () => any);

    onResize(handler: (width: number, height: number) => void);

    cancel(handler: () => void);

    onKeyDown(handler: (e: JQuery.Event) => boolean); // handler returns true if ket is handled
    getControl(): JQuery;

    getContent(): JQuery;

    resize(parameter?: any);

    toggleAction(toggleValue);

    registerValidationControls(controls);

    registerValidationControls(controls: any[]);

    setTitleText(titleText: string);

    setOkButtonState(state: boolean);

    setOkButtonText(text: string);

    spinnerStart(param?: any);

    spinnerStop();

    destroy();

    uniqueId(): string;

    toggleFullScreen(toFullScreen: boolean): void;

    triggerOk(): void;
}

export function Dialog(param: IDialogParameters): IDialog {
    return dialog(param);
}

export function dialog(param: IDialogParameters): IDialog {
    param = $.extend(
        {
            css: null,
            className: '',
            isModal: true,
            fullScreen: false,

            hideOk: false,
            hideCancel: true,
            cancelText: strings.cancel,
            okText: strings.ok,
            title: strings.dialog,
            onClose: _.noop,

            useDetach: false,

            width: 'auto',
            height: 'auto',
            minWidth: 100,
            minHeight: 100,
            maxWidth: 'auto',
            contentWidth: null,
            contentHeight: null,

            closeByEnter: true,
            closeByEsc: true,

            usePaddings: true,
            noHeaderLine: false,

            buttons: [],

            clipToWindow: false,
            canSwitchToFullScreen: false,
            canClose: true,
            resizable: false,
            resizeOnFly: true,
            useResizeLock: false,
            destroyWraped: true,
            parentViewAppearance: null
        },
        param
    );

    let ok = _.noop;
    let cancel = _.noop;
    let onResize = _.noop;
    let onClose = _.noop;
    let onKeyDown = _.noop;

    let wasResized = false;
    let lockResize = false;
    let afterResizeState = null;

    const resizeHelperSize = 6;
    const uniqueId = Guid.newGuid();

    let view;
    let control;
    let popup: IPopupControl;

    let validationControls = [];

    // resize
    WindowResizeHelper.on(resize);

    // root
    const root = $('<div class="dialog dialog-text"/>')
        .css({
            minWidth: param.minWidth,
            minHeight: param.minHeight,
            maxWidth: param.maxWidth
        })
        .toggleClass('no-modal popup-shadow', !param.isModal)
        .toggleClass('no-gap', !param.usePaddings)
        .attr('tabindex', -1)
        .on('keydown', function (e) {
            if (param.isModal) {
                e.stopPropagation();
            }
            if (onKeyDown(e)) {
                return;
            }
            if (e.which == constants.keyCodes.ENTER && param.closeByEnter && (param.hideOk || okButton.getState())) {
                onOk(e);
            }
            if (e.which == constants.keyCodes.ESCAPE && param.closeByEsc) {
                onCancel(e);
            }
        });

    // header
    const header = $('<div class="dialog-header"/>').appendTo(root);
    param.canSwitchToFullScreen && !param.fullScreen && header.doubleClick(fullModeToggle);

    if (!param.fullScreen) {
        root.draggable({
            handle: header,
            scroll: false,
            cancel: '.button-wrapper',
            containment: param.clipToWindow ? 'window' : false
        });

        if (param.resizable) {
            root.resizable({
                minWidth: param.minWidth,
                minHeight: param.minHeight,
                start: function () {
                    afterResizeState = null;
                    wasResized = true;
                    param.useResizeLock && (lockResize = true);
                },
                stop: function () {
                    lockResize = false;

                    afterResizeState = {
                        top: root.offset().top,
                        left: root.offset().left,
                        width: root.outerWidth(true),
                        height: root.outerHeight(true)
                    };
                    resize();
                },
                resize: param.resizeOnFly ? resize.bind(this) : _.noop
            });
        }
    }

    // dialog headers buttons
    const buttonWrapper = $('<div class="button-wrapper"/>').appendTo(header);
    const title = $('<div class="dialog-title text-no-wrap"/>').appendTo(header);
    const line = $('<div class="dialog-header-line"/>').toggleClass('hidden', param.noHeaderLine).appendTo(header);

    setTitleText(param.title);

    const maxScreenBtn = $('<span class="full-screen dialog-button"/>');
    param.canSwitchToFullScreen && maxScreenBtn.appendTo(buttonWrapper).click(fullModeToggle);

    const minScreenBtn = $('<span class="min-screen dialog-button"/>');
    !param.fullScreen && minScreenBtn.appendTo(buttonWrapper).click(fullModeToggle);

    const closeBtn = $('<span class="close dialog-button"/>');
    param.canClose &&
        closeBtn.appendTo(buttonWrapper).on('click ' + pointerHelper.pointerDownEventName, function (e) {
            onCancel(e);
        });

    // Bug 665127
    if (param.parentViewAppearance) {
        const appearance = param.parentViewAppearance;

        root.addClass(param.stylesHelper.getStyleName(appearance.dialogBorderStyleName));
        header.addClass(param.stylesHelper.getStyleName(appearance.dialogTitleStyleName)).css({ padding: 0 });

        const headerLineStyle = param.stylesHelper.getStyle(appearance.dialogHeaderLineStyleName);
        line.css({
            backgroundColor: headerLineStyle.borderColor,
            height: headerLineStyle.borderWidth,
            padding: `0 ${appearance.dialogHeaderLinePadding}px`
        });

        title.addClass(param.stylesHelper.getStyleName(appearance.dialogTitleStyleName)).css({ height: 'auto' });
        maxScreenBtn.addClass(param.stylesHelper.getStyleName(appearance.dialogMaximizeButtonImageStyleName)).css({
            padding: 0,
            margin: param.stylesHelper.getStyle(appearance.dialogMaximizeButtonImageStyleName).padding || '0px'
        });
        minScreenBtn.addClass(param.stylesHelper.getStyleName(appearance.dialogMinimizeButtonImageStyleName)).css({
            padding: 0,
            margin: param.stylesHelper.getStyle(appearance.dialogMinimizeButtonImageStyleName).padding || '0px'
        });
        closeBtn.addClass(param.stylesHelper.getStyleName(appearance.dialogCloseButtonImageStyleName)).css({
            padding: 0,
            margin: param.stylesHelper.getStyle(appearance.dialogCloseButtonImageStyleName).padding || '0px'
        });
    }

    // content
    const content = $('<div class="dialog-content"/>').appendTo(root);
    if (param.className) {
        content.addClass(param.className);
    }
    if (param.css) {
        content.css(param.css);
    }

    // actions
    const actions = $('<div class="actions"/>')
        .filter(function () {
            return !param.hideOk || !param.hideCancel || (param.buttons && param.buttons.length > 0);
        })
        .appendTo(root)
        .end();

    setDialogSize(true);

    const cancelButton = new Button({ text: param.cancelText });
    cancelButton
        .getControl()
        .filter(function () {
            return !param.hideCancel;
        })
        .appendTo(actions);
    cancelButton.onClick(function (e) {
        onCancel(e);
    });

    const buttons = [];
    _.forEach(param.buttons, function (button) {
        let buttonControl;
        if (button instanceof Button) {
            buttonControl = button;
        } else {
            buttonControl = new Button({
                id: button.id, ///????
                text: button.text,
                width: button.width,
                enabled: button.enabled
            } as any);

            buttonControl.onClick(function (e) {
                button.ready && button.ready(buttonControl);
                e.preventDefault();
            });
        }

        buttons.push(buttonControl);
        buttonControl.getControl().appendTo(actions);
    });

    const okButton = new Button({ text: param.okText, className: 'accept' });
    if (!param.hideOk) {
        // OS Win has Ok - Cancel sequence, other OS have Cancel - Ok
        if (navigator.platform && navigator.platform.indexOf('Win') === 0) {
            okButton.getControl().prependTo(actions);
        } else {
            okButton.getControl().appendTo(actions);
        }
    }
    okButton.onClick(function (e) {
        onOk(e);
    });

    function onOk(e) {
        e.preventDefault();

        const result = ok();
        if (result == undefined) {
            remove();
        } else if (commonUtils.isPromise(result)) {
            result.done(function () {
                remove();
            });
        } else if (result instanceof Promise) {
            result.then(remove);
        }
    }

    function onCancel(e) {
        e.preventDefault();
        remove();
        cancel();
        param.onClose();
    }

    function fullModeToggle() {
        if (!root.hasClass('full-screen')) {
            param.height = root.outerHeight(true);
            param.width = root.outerWidth(true);

            root.addClass('full-screen').fullScreen().center();
            content.css({
                width: '100%',
                height: '100%',
                boxSizing: 'border-box'
            });
            resize();

            root.draggable('disable');
            param.resizable && root.resizable('disable');
        } else {
            root.removeClass('full-screen');
            resize();
            if (!param.fullScreen) {
                root.draggable('enable');
                param.resizable && root.resizable('enable');
            }

            root.center();
        }
    }

    function setDialogSize(isInitial?) {
        // set size if dialog not resized
        if (wasResized) {
            if (afterResizeState) {
                root.css({
                    width: afterResizeState.width,
                    height: afterResizeState.height
                });
            }
        } else {
            root.css({
                width: param.width,
                height: param.height
            });

            if (param.contentWidth) {
                content.css('width', param.contentWidth);
            }
            if (param.contentHeight) {
                content.css({
                    height: param.contentHeight
                });
            }
        }

        const checkWidth = content.outerWidth() + root.borderWidth() > $(window).width() - resizeHelperSize;
        if (checkWidth) {
            const w = $(window).width() - resizeHelperSize;
            root.css('width', w);
            content.css('width', '100%');
        }

        const checkHeight = content.outerHeight() + root.borderHeight() > $(window).height() - resizeHelperSize;
        if (checkHeight) {
            const h = $(window).height() - resizeHelperSize;
            root.css('height', h);
            content.css({
                height: '100%',
                boxSizing: 'border-box'
            });
        }

        header.find('.full-screen.dialog-button').css('visibility', checkWidth && checkHeight ? 'hidden' : 'visible');
        header.find('.min-screen.dialog-button').css('visibility', checkWidth && checkHeight ? 'hidden' : 'visible');
    }

    function resize(parameter?) {
        // lock resize
        if (lockResize) {
            return;
        }

        if (root.hasClass('full-screen')) {
            // if full screen adjust content
            content.css({
                width: '100%',
                height: '100%'
            });
            root.fullScreen();
        } else {
            // if dialog is not full screen mode and canSwitchToFullScreen, then check dialog size
            setDialogSize();
        }

        const cw = content.width();
        const ch = content.height();

        if (view) {
            view.adjust(cw, ch, parameter);
        } else if (control) {
            control.adjust(cw, ch);
        }

        onResize(cw, ch);

        // center dialog on screen
        if (!wasResized) {
            root.center();
        }
    }

    function validate() {
        _.forEach(validationControls, function (validationControl) {
            validationControl.control.validate && validationControl.control.validate();
        });
    }

    root.toggleClass('full-screen', param.fullScreen);

    if (param.fullScreen) {
        root.fullScreen();
        resize();
    }

    function remove() {
        if (param.useDetach) {
            root.detach();
        } else {
            destroy();
            root.remove();
        }

        popup && popup.close();

        commonUtils.onElementBlurHandler();

        onClose();
    }

    function wrap(obj) {
        if (obj && obj.getControl) {
            const isTabs = obj.isTabsControl && obj.isTabsControl();
            root.toggleClass('no-gap', isTabs == true); // According to new design;

            control = obj;
            content.append(obj.getControl());
        } else if (_.isArray(obj)) {
            _.forEach(obj, function (item) {
                content.append(item);
            });
        } else {
            content.append(obj);
        }

        if (param.isModal) {
            popup = commonUtils.createPopUp({ opacity: true });
        }

        root.hide().appendTo('body').center().show().focus();
        validate();

        // if control is controls, call adjust
        if (obj.adjust) {
            obj.adjust(content.realSize().width);
        }

        resize();
    }

    function wrapView(v) {
        view = v;
        content.append(v.getControl());
        if (!param.fullScreen) {
            popup = commonUtils.createPopUp({ opacity: true });
        }
        root.hide().appendTo('body').center().show().focus();
        v.setInitialSize(content.width(), content.height());
    }

    function center() {
        root.center();
    }

    function getButton(id) {
        if (!id) {
            return null;
        }
        const btn = _.find(buttons, function (el) {
            return el.id() === id;
        });
        return btn;
    }

    function getActualHeight() {
        return root.height();
    }

    let validationMode = dialogValidationMode.none;

    function registerValidationControls(controls) {
        if (validationMode == dialogValidationMode.none || validationMode == dialogValidationMode.complex) {
            validationMode = dialogValidationMode.complex;

            validationControls = _.map(controls, function (control) {
                return {
                    control: control,
                    state: true
                };
            });

            _.forEach(validationControls, function (validationControl) {
                validationControl.control.onValidate &&
                    validationControl.control.onValidate(function (state) {
                        validationControl.state = state;
                        processValidation();
                    });
            });
        }
    }

    function processValidation() {
        const isValid = !_.find(validationControls, function (validationControl) {
            return !validationControl.state;
        });

        okButton.setState(isValid);
    }

    function setOkButtonState(state) {
        if (validationMode == dialogValidationMode.none || validationMode == dialogValidationMode.simple) {
            validationMode = dialogValidationMode.simple;
            okButton.setState(state);
        }
    }

    function setOkButtonText(text) {
        okButton.setText(text);
    }

    function setTitleText(titleText) {
        title.text(titleText);
    }

    function destroy() {
        WindowResizeHelper.off(resize);

        if (param.destroyWraped) {
            safeDestroys(control);
        }

        safeDestroys(cancelButton, okButton, ...buttons);
    }

    return {
        wrap: wrap,
        wrapView: wrapView,
        center: center,
        getButton: getButton,
        getActualHeight: getActualHeight,
        remove: remove,
        close: remove,
        onClose: function (fn) {
            if ($.isFunction(fn)) {
                onClose = fn;
            }
        },
        ok: function (fn) {
            if ($.isFunction(fn)) {
                ok = fn;
            }
        },
        onResize: function (fn) {
            if ($.isFunction(fn)) {
                onResize = fn;
            }
        },
        cancel: function (fn) {
            if ($.isFunction(fn)) {
                cancel = fn;
            }
        },
        onKeyDown: function (fn) {
            if ($.isFunction(fn)) {
                onKeyDown = fn;
            }
        },
        getControl: function () {
            return root;
        },
        getContent: function () {
            return content;
        },
        resize: resize,
        toggleAction: function (toggleValue) {
            actions.toggle(toggleValue);
        },
        registerValidationControls: registerValidationControls,
        setTitleText: setTitleText,
        spinnerStart: function (param) {
            content.spinnerStart(param);
        },
        spinnerStop: function () {
            content.spinnerStop();
        },
        setOkButtonState: setOkButtonState,
        setOkButtonText: setOkButtonText,
        destroy: destroy,
        uniqueId: function () {
            return uniqueId;
        },
        toggleFullScreen: function (toFullScreen) {
            if (root.hasClass('full-screen') !== toFullScreen) {
                fullModeToggle();
            }
        },
        triggerOk: function () {
            const event = document.createEvent('HTMLEvents');
            event.initEvent('click');
            onOk(event);
        }
    };
}

export const dialogValidationMode = {
    none: 'none',
    simple: 'simple',
    complex: 'complex'
};
